import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { EndPoints } from '../../axios/endPoints';
import { axiosInstance } from '../../axios/axiosHandler';
import { authHeader } from '../auth-slice/auth-slice';

// Async thunk to fetch prediction columns for a specific training dataset
export const GetPredictionColumns = createAsyncThunk(
    'GetPredictionColumns',
    async (id: number, thunkAPI) => {
        try {
            // Send a GET request to fetch prediction columns
            const response = await axiosInstance.get(
                EndPoints.ML_CREATOR_GET_TRAINING_MODEL_DETAILS + `?id=${id}`,
                authHeader()
            );
            return await response.data;
        } catch (error) {
            // Return a rejected value if the request fails
            return thunkAPI.rejectWithValue(null);
        }
    }
);

// Async thunk to predict a single input
export const PredictSingle = createAsyncThunk(
    'PredictSingle',
    async (request: any, thunkAPI) => {
        try {
            // Send a POST request to predict a single input
            const response = await axiosInstance.post(
                EndPoints.ML_TESTER_PREDICT_SINGLE,
                request,
                authHeader()
            );
            return await response.data;
        } catch (error) {
            // Return a rejected value if the request fails
            return thunkAPI.rejectWithValue(null);
        }
    }
);

// Async thunk to fetch all prediction models
export const GetPredictionModels = createAsyncThunk(
    'GetPredictionModels',
    async (_, thunkAPI) => {
        try {
            // Send a GET request to fetch prediction models
            const response = await axiosInstance.get(
                EndPoints.PREDICTION_GET_MODELS,
                authHeader()
            );
            return await response.data;
        } catch (error) {
            // Return a rejected value if the request fails
            return thunkAPI.rejectWithValue(null);
        }
    }
);

// Async thunk to delete a prediction by ID
export const DeletePrediction = createAsyncThunk(
    'DeletePrediction',
    async (id: number, thunkAPI) => {
        try {
            // Send a POST request to delete a prediction
            const response = await axiosInstance.post(
                EndPoints.DELETE_PREDICTION + `?id=${id}`,
                null,
                authHeader()
            );
            return await response.data;
        } catch (error) {
            // Return a rejected value if the request fails
            return thunkAPI.rejectWithValue(null);
        }
    }
);

// Async thunk to fetch details of a specific prediction
export const GetPredictionDetails = createAsyncThunk(
    'GetPredictionDetails',
    async (id: number, thunkAPI) => {
        try {
            // Send a GET request to fetch prediction details
            const response = await axiosInstance.get(
                EndPoints.GET_PREDICTION_DETAILS + `?id=${id}`,
                authHeader()
            );
            return await response.data;
        } catch (error) {
            // Return a rejected value if the request fails
            return thunkAPI.rejectWithValue(null);
        }
    }
);

// Create a Redux slice for managing prediction-related state
export const predictionSlice = createSlice({
    name: 'prediction',
    initialState: {
        // Define initial state properties
        predictionColumnsLoading: true,
        predictionDataInputColumns: [] as any[],
        predictionInProgress: false,
        predictionTrainingDatasetId: null,
        predictionIsShared: false,
        predictionIsDisabled: false,
        singlePredictionResult: null as any,
        singlePredictionResultDialogOpen: false,
        predictionModels: [],
        deletePredictionDialog: { isOpen: false, body: null },
        deletingPrediction: false,
        refreshPredictionsTable: 0,
        predictionDetailsDialog: { isOpen: false, body: null },
        predictionDetailsLoading: true,
        predictionDetails: null
    },
    reducers: {
        // Clear all prediction-related data
        clearPredictionData: (state) => {
            state.predictionDataInputColumns = [];
            state.singlePredictionResult = null;
            state.singlePredictionResultDialogOpen = false;
            state.predictionTrainingDatasetId = null;
            state.predictionIsShared = false;
            state.predictionIsDisabled = false;
            state.predictionModels = [];
        },
        // Save a single prediction input value
        savePredictionValue: (state, action) => {
            const { name, value } = action.payload;
            state.predictionDataInputColumns = state.predictionDataInputColumns.map((el) =>
                el.name === name ? { ...el, value } : el
            );
        },
        // Toggle visibility of the single prediction result dialog
        handleToggleSinglePredictionDialog: (state, action) => {
            state.singlePredictionResultDialogOpen = action.payload;
        },
        // Clear results of a single prediction
        handleClearSinglePredictionResults: (state) => { 
            state.singlePredictionResult = null;
        },
        // Clear all input column values
        handleClearAllInputColumns: (state) => {
            state.predictionDataInputColumns = state.predictionDataInputColumns.map((el) => ({
                ...el,
                value: null
            }));
        },
        // Toggle visibility of the delete prediction dialog
        handleToggleDeletePredictionDialog: (state, action) => {
            state.deletePredictionDialog = action.payload;
        },
        // Toggle visibility of the prediction details dialog
        handleTogglePredictionDetailsDialog: (state, action) => {
            state.predictionDetailsDialog = action.payload;
        },
    },
    extraReducers: (builder) => {
        // Add cases for async thunk actions to manage state transitions
        builder.addCase(GetPredictionColumns.pending, (state) => { 
            state.predictionColumnsLoading = true;
        });
        builder.addCase(GetPredictionColumns.fulfilled, (state, action) => { 
            state.predictionColumnsLoading = false;
            if(action.payload?.success) {
                const result = action.payload.result;
                if (result) {
                    state.predictionDataInputColumns = result.inputColumnsDetails.map((el: any) => ({ 
                        ...el, 
                        value: null 
                    }));
                    state.predictionTrainingDatasetId = result.request?.mlTrainingDatasetId;
                    state.predictionIsShared = result.isShared;
                    state.predictionIsDisabled = result.isDisabled;
                }
            }
        });
        builder.addCase(GetPredictionColumns.rejected, (state) => { 
            state.predictionColumnsLoading = false;
        });

        builder.addCase(PredictSingle.pending, (state) => { 
            state.predictionInProgress = true;
        });
        builder.addCase(PredictSingle.fulfilled, (state, action) => { 
            state.predictionInProgress = false;
            const result = action.payload?.result;
            if(result?.success) {
                state.refreshPredictionsTable++;
                state.singlePredictionResult = {
                    startPredictDateTimeUTC: result.startPredictDateTimeUTC,
                    endPredictDateTimeUTC: result.endPredictDateTimeUTC,
                    predictDuration: result.predictDuration,
                    predictionResult: result.predictionResult
                };
                state.singlePredictionResultDialogOpen = true;
            }
        });
        builder.addCase(PredictSingle.rejected, (state) => { 
            state.predictionInProgress = false;
        });
        
        builder.addCase(GetPredictionModels.fulfilled, (state, action) => { 
            state.predictionModels = action.payload || [];
        });
        
        builder.addCase(DeletePrediction.pending, (state) => { 
            state.deletingPrediction = true;
        });
        builder.addCase(DeletePrediction.fulfilled, (state) => { 
            state.deletingPrediction = false;
            state.deletePredictionDialog = { isOpen: false, body: null };
            state.refreshPredictionsTable++;
        });
        builder.addCase(DeletePrediction.rejected, (state) => { 
            state.deletingPrediction = false;
        });

        builder.addCase(GetPredictionDetails.pending, (state) => { 
            state.predictionDetailsLoading = true;
        });
        builder.addCase(GetPredictionDetails.fulfilled, (state, action) => { 
            state.predictionDetailsLoading = false;
            if(action.payload?.success) {
                state.predictionDetails = action.payload.result;
            }
        });
        builder.addCase(GetPredictionDetails.rejected, (state) => { 
            state.predictionDetailsLoading = false;
            state.predictionDetails = null;
        });
    },
});

// Export actions for dispatching
export const { 
    clearPredictionData,
    savePredictionValue,
    handleToggleSinglePredictionDialog,
    handleClearSinglePredictionResults,
    handleClearAllInputColumns,
    handleToggleDeletePredictionDialog,
    handleTogglePredictionDetailsDialog,
} = predictionSlice.actions;

// Selector to access prediction slice state
export const predictionSelector = (state: RootState) => state.prediction;